import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Button,
  useTheme,
  Divider,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import NotificationPanel from '../NotificationPanel'
import { NAV_CHROME as N } from './navChrome'

const Header = ({ onMenuClick }) => {
  const theme = useTheme()
  const { user, logout, getUserRole } = useAuth()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [logoError, setLogoError] = useState(false)

  const roleName = getUserRole?.() || user?.role || 'ADMIN'

  React.useEffect(() => {
    const handleOpenNotifications = () => setNotificationOpen(true)
    window.addEventListener('openNotifications', handleOpenNotifications)
    return () => window.removeEventListener('openNotifications', handleOpenNotifications)
  }, [])

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget)
  const handleProfileMenuClose = () => setAnchorEl(null)
  const handleLogout = () => {
    logout()
    handleProfileMenuClose()
  }

  const glassBtn = {
    color: N.text,
    width: 38,
    height: 38,
    borderRadius: '10px',
    bgcolor: N.glass,
    border: `1px solid ${N.glassBorder}`,
    backdropFilter: 'blur(10px)',
    transition: 'background 0.18s ease, border-color 0.18s ease, color 0.18s ease',
    '&:hover': {
      bgcolor: N.glassHover,
      borderColor: N.yellowBorder,
      color: N.yellow,
    },
  }

  return (
    <AppBar
      className="app-header"
      position="static"
      elevation={0}
      sx={{
        background: N.headerGradient,
        borderBottom: `1px solid ${N.border}`,
        color: N.text,
        boxShadow: N.shadow,
        height: { xs: 64, md: N.headerHeight },
        minHeight: { xs: 64, md: N.headerHeight },
        maxHeight: { xs: 64, md: N.headerHeight },
        overflow: 'hidden',
      }}
    >
      <Toolbar
        sx={{
          minHeight: '64px !important',
          height: { xs: 64, md: N.headerHeight },
          maxHeight: { xs: 64, md: N.headerHeight },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1, md: 1.5 },
          flexWrap: 'nowrap',
          px: { xs: 1.25, sm: 2 },
          py: 0,
        }}
      >
        {/* Brand — EduOKWeb crm-header-brand */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '1 1 0',
            minWidth: 0,
            gap: 1,
          }}
        >
          <IconButton edge="start" aria-label="menu" onClick={onMenuClick} sx={glassBtn}>
            <MenuIcon fontSize="small" />
          </IconButton>

          <Box
            component="div"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: N.glass,
              border: `1px solid ${N.glassBorder}`,
              px: 0.75,
              py: 0.5,
              borderRadius: '10px',
              transition: 'background 0.18s ease, border-color 0.18s ease',
              '&:hover': {
                bgcolor: N.glassHover,
                borderColor: N.yellowBorder,
              },
            }}
          >
            {!logoError ? (
              <Box
                component="img"
                src="/eduok-logo.png"
                alt="EduOK"
                onError={() => setLogoError(true)}
                sx={{
                  width: 36,
                  height: 36,
                  objectFit: 'contain',
                  borderRadius: '8px',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  bgcolor: N.yellow,
                  color: N.ink,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                }}
              >
                OK
              </Box>
            )}
            <Typography
              sx={{
                m: 0,
                color: N.text,
                fontWeight: 750,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                letterSpacing: '0.02em',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              EduOK CRM
            </Typography>
          </Box>
        </Box>

        {/* Center — EduOKWeb crm-header-center */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flex: '0 1 auto',
            maxWidth: '48%',
            minWidth: 0,
            justifyContent: 'center',
            alignItems: 'center',
            px: 1,
          }}
        >
          <Box sx={{ minWidth: 0, textAlign: 'center' }}>
            <Typography
              sx={{
                m: 0,
                color: N.text,
                fontWeight: 750,
                fontSize: { md: '1rem', lg: '1.125rem' },
                letterSpacing: '0.02em',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              EduOK School Management System
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: '4px',
                px: 1.25,
                py: 0.35,
                bgcolor: N.glass,
                border: `1px solid ${N.glassBorder}`,
                borderRadius: '10px',
                maxWidth: '100%',
              }}
            >
              <Typography
                sx={{
                  m: 0,
                  color: N.text,
                  fontSize: { md: '0.95rem', lg: '1.1rem' },
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                EduOK Gold
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* User — EduOKWeb crm-header-user */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.25,
            flex: '1 1 0',
            minWidth: 0,
          }}
        >
          <IconButton onClick={() => setNotificationOpen(true)} sx={glassBtn} aria-label="Notifications">
            <Badge
              badgeContent={3}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: N.yellow,
                  color: N.ink,
                  fontWeight: 700,
                },
              }}
            >
              <NotificationsIcon fontSize="small" />
            </Badge>
          </IconButton>

          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: N.glass,
              border: `1px solid ${N.glassBorder}`,
              borderRadius: '10px',
              px: 0.75,
              py: 0.5,
              cursor: 'pointer',
              transition: 'background 0.18s ease, border-color 0.18s ease',
              '&:hover': {
                bgcolor: N.glassHover,
                borderColor: N.yellowBorder,
              },
            }}
          >
            <Avatar
              src={user?.avatar}
              sx={{
                width: 32,
                height: 32,
                bgcolor: N.yellow,
                color: N.ink,
                fontWeight: 800,
                fontSize: '0.8rem',
                border: '2px solid rgba(255,176,32,0.45)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontWeight: 650, color: N.text, fontSize: '0.875rem', lineHeight: 1.2 }}
              >
                {user?.name || 'User'}
              </Typography>
              <Typography
                noWrap
                sx={{ fontWeight: 500, color: N.textMuted, fontSize: '0.75rem', lineHeight: 1.2 }}
              >
                {roleName}
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              bgcolor: N.glass,
              border: `1px solid ${N.glassBorder}`,
              borderRadius: '12px',
              px: 1.75,
              py: 1,
              color: N.text,
              fontWeight: 650,
              fontSize: '0.8125rem',
              textTransform: 'none',
              minWidth: 0,
              transition: 'background 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease',
              '&:hover': {
                bgcolor: N.yellowSoft,
                borderColor: N.yellowBorder,
                color: N.yellow,
                transform: 'translateY(-1px)',
              },
            }}
          >
            Logout
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: '0 8px 24px rgba(0,45,40,0.18)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                p: 1,
              },
            }}
          >
            <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {user?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {user?.email}
              </Typography>
            </Box>

            <MenuItem
              onClick={handleProfileMenuClose}
              sx={{
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'linear-gradient(135deg, rgba(249,166,2,0.2) 0%, rgba(231,73,0,0.1) 100%)',
                  color: N.bright,
                },
              }}
            >
              <PersonIcon sx={{ mr: 1.5, fontSize: 18 }} />
              Profile
            </MenuItem>

            <MenuItem
              onClick={handleProfileMenuClose}
              sx={{ borderRadius: '8px', fontSize: '0.75rem', fontWeight: 500 }}
            >
              <SettingsIcon sx={{ mr: 1.5, fontSize: 18 }} />
              Settings
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={handleLogout}
              sx={{ borderRadius: '8px', color: 'error.main', fontSize: '0.75rem', fontWeight: 500 }}
            >
              <LogoutIcon sx={{ mr: 1.5, fontSize: 18 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <NotificationPanel open={notificationOpen} onClose={() => setNotificationOpen(false)} />
    </AppBar>
  )
}

export default Header
